/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useContext, useRef } from "react";
import {
  Bell,
  ChevronRight,
  Shield,
  Moon,
  Camera,
  Calendar,
  CalendarDays,
  Eye,
  EyeOff,
} from "lucide-react";
import { AdminContext } from "../../../context/AdminContext";
import { toast } from "react-toastify";
import { useTheme } from "../../../context/ThemeContext";
import { tabs } from "../../../utils/constants";
import placeholderImage from "../../../assets/placeholder.webp";

const Settings = () => {
  const { adminProfile, fetchAdminProfile, updateAdminProfile } =
    useContext(AdminContext);
  const { isDarkMode, toggleDarkMode } = useTheme();

  const [activeTab, setActiveTab] = useState("account");
  const [emailNotif, setEmailNotif] = useState(true);
  const [smsNotif, setSmsNotif] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [modalType, setModalType] = useState("");
  const [passwordStrength, setPasswordStrength] = useState(0);
  const [showPasswords, setShowPasswords] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });
  const [selectedImage, setSelectedImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const fileInputRef = useRef(null);

  // Form states
  const [profileForm, setProfileForm] = useState({
    name: "",
    email: "",
    phone: "",
    addressLine1: "",
    addressLine2: "",
    gender: "",
    dateOfBirth: "",
  });

  const [securityForm, setSecurityForm] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  useEffect(() => {
    fetchAdminProfile();
  }, [fetchAdminProfile]);

  useEffect(() => {
    if (adminProfile) {
      setProfileForm({
        name: adminProfile.name || "",
        email: adminProfile.email || "",
        phone: adminProfile.phoneNumber || "",
        addressLine1: adminProfile.address?.line1 || "",
        addressLine2: adminProfile.address?.line2 || "",
        gender: adminProfile.gender || "",
        dateOfBirth: adminProfile.dob ? adminProfile.dob.split("T")[0] : "",
      });
      setImagePreview(adminProfile.image || null);
      setSelectedImage(null);
    }
  }, [adminProfile]);

  const handleAvatarChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select an image file");
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size should be less than 5MB");
        return;
      }
      setSelectedImage(file);
      setImagePreview(URL.createObjectURL(file));
    }
  };

  const handleRemoveAvatar = () => {
    setSelectedImage(null);
    setImagePreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleProfileChange = (e) => {
    setProfileForm({ ...profileForm, [e.target.name]: e.target.value });
  };

  const handleSecurityChange = (e) => {
    setSecurityForm({ ...securityForm, [e.target.name]: e.target.value });
    if (e.target.name === "newPassword") {
      const strength =
        e.target.value.length >= 8 ? 100 : (e.target.value.length / 8) * 100;
      setPasswordStrength(strength);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords({
      ...showPasswords,
      [field]: !showPasswords[field],
    });
  };

  const handleConfirmAction = (type) => {
    setModalType(type);
    setShowConfirmModal(true);
  };

  const handleModalAction = (confirmed) => {
    if (confirmed) {
      switch (modalType) {
        case "updateProfile":
          updateAdminProfile(
            adminProfile.id,
            profileForm.name,
            profileForm.email,
            profileForm.phone,
            {
              line1: profileForm.addressLine1,
              line2: profileForm.addressLine2,
            },
            profileForm.gender,
            profileForm.dateOfBirth,
            selectedImage, // Pass the image file
          );
          // Clear selected image after save
          setSelectedImage(null);
          if (fileInputRef.current) {
            fileInputRef.current.value = "";
          }
          setIsEditing(false);
          break;
        case "updatePassword":
          handleUpdatePassword();
          break;
        case "cancel":
          // Reset forms
          if (adminProfile) {
            setProfileForm({
              name: adminProfile.name || "",
              email: adminProfile.email || "",
              phone: adminProfile.phoneNumber || "",
              addressLine1: adminProfile.address?.line1 || "",
              addressLine2: adminProfile.address?.line2 || "",
              gender: adminProfile.gender || "",
              dateOfBirth: adminProfile.dob
                ? adminProfile.dob.split("T")[0]
                : "",
            });
            setImagePreview(adminProfile.image || null);
            setSelectedImage(null);
            if (fileInputRef.current) {
              fileInputRef.current.value = "";
            }
          }
          setSecurityForm({
            currentPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
          setIsEditing(false);
          break;
        default:
          break;
      }
    }
    setShowConfirmModal(false);
  };

  const handleUpdatePassword = () => {
    if (
      !securityForm.currentPassword ||
      !securityForm.newPassword ||
      !securityForm.confirmPassword
    ) {
      toast.error("All password fields are required.");
      return;
    }
    if (securityForm.newPassword !== securityForm.confirmPassword) {
      toast.error("New password and confirm password do not match.");
      return;
    }
    if (securityForm.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters long.");
      return;
    }

    updateAdminProfile(
      adminProfile.id,
      null,
      null,
      null,
      null,
      null,
      null,
      null, // no image file when changing only the password
      securityForm.currentPassword,
      securityForm.newPassword,
      securityForm.confirmPassword,
    );

    // Reset security form
    setSecurityForm({
      currentPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setPasswordStrength(0);
  };

  const handleNotificationToggle = (type) => {
    if (type === "email") {
      setEmailNotif(!emailNotif);
      toast.success(
        `Email notifications ${!emailNotif ? "enabled" : "disabled"}`,
      );
    } else if (type === "sms") {
      setSmsNotif(!smsNotif);
      toast.success(`SMS notifications ${!smsNotif ? "enabled" : "disabled"}`);
    }
  };

  const renderPasswordStrength = () => {
    if (securityForm.newPassword.length === 0) return null;

    let color = "bg-red-500";
    let text = "Weak";

    if (passwordStrength >= 50 && passwordStrength < 100) {
      color = "bg-yellow-500";
      text = "Medium";
    } else if (passwordStrength >= 100) {
      color = "bg-green-500";
      text = "Strong";
    }

    return (
      <div className="mt-2 space-y-1">
        <div className="flex justify-between text-xs">
          <span className="text-gray-500 dark:text-dark-muted">
            Password strength:
          </span>
          <span
            className={`font-medium ${
              passwordStrength < 50
                ? "text-red-500"
                : passwordStrength < 100
                  ? "text-yellow-500"
                  : "text-green-500"
            }`}
          >
            {text}
          </span>
        </div>
        <div className="h-1.5 w-full bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div
            className={`h-full transition-all duration-300 ${color}`}
            style={{ width: `${passwordStrength}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className={`${isDarkMode ? "dark" : ""}`}>
      <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 max-w-6xl mx-auto pb-12 dark:bg-dark-background min-h-screen p-4">
        {/* Modern Header */}
        <div className="mb-10 relative overflow-hidden rounded-3xl bg-gradient-to-r from-primary to-secondary dark:from-gray-950 dark:to-gray-900 p-10 text-white shadow-2xl">
          <div className="relative z-10">
            <h1 className="text-4xl font-bold mb-3">Settings</h1>
            <p className="text-gray-300 dark:text-gray-300 max-w-lg">
              Manage your account preferences, security settings, and billing
              information all in one place.
            </p>
          </div>
          <div className="absolute top-0 right-0 w-64 h-64 bg-primary/20 rounded-full blur-3xl -mr-16 -mt-16 pointer-events-none"></div>
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-blue-500/10 rounded-full blur-3xl -ml-10 -mb-10 pointer-events-none"></div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* Navigation Sidebar */}
          <div className="lg:col-span-3 space-y-3">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center gap-4 p-4 rounded-2xl transition-all duration-300 text-left border ${
                    isActive
                      ? "bg-card dark:bg-dark-surface border-gray-200 dark:border-dark-border shadow-lg scale-[1.02]"
                      : "bg-transparent border-transparent hover:bg-card/50 dark:hover:bg-dark-surface/50 text-gray-500 dark:text-dark-muted"
                  }`}
                >
                  <div
                    className={`p-2.5 rounded-xl ${
                      isActive
                        ? "text-primary dark:text-primary"
                        : "text-gray-500 dark:text-dark-muted"
                    }`}
                  >
                    <Icon size={20} />
                  </div>
                  <div>
                    <p
                      className={`font-bold ${
                        isActive
                          ? "text-text dark:text-dark-text"
                          : "text-gray-600 dark:text-dark-muted"
                      }`}
                    >
                      {tab.label}
                    </p>
                    <p className="text-xs text-gray-400 dark:text-dark-muted">
                      {tab.desc}
                    </p>
                  </div>
                  {isActive && (
                    <ChevronRight
                      size={16}
                      className="ml-auto text-primary dark:text-primary"
                    />
                  )}
                </button>
              );
            })}

            {/* Dark Mode Toggle */}
            <div className="mt-8 p-4 bg-card dark:bg-dark-surface rounded-2xl border border-gray-100 dark:border-dark-border">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Moon
                    size={20}
                    className="text-gray-600 dark:text-dark-muted"
                  />
                  <div>
                    <p className="font-medium text-text dark:text-dark-text">
                      Dark Mode
                    </p>
                    <p className="text-xs text-gray-500 dark:text-dark-muted">
                      Toggle theme
                    </p>
                  </div>
                </div>
                <button
                  onClick={toggleDarkMode}
                  className={`w-14 h-8 rounded-full p-1 transition-colors duration-300 ease-in-out ${
                    isDarkMode ? "bg-primary" : "bg-gray-300 dark:bg-gray-700"
                  }`}
                >
                  <div
                    className={`w-6 h-6 rounded-full bg-white shadow-md transform transition-transform duration-300 ${
                      isDarkMode ? "translate-x-6" : "translate-x-0"
                    }`}
                  ></div>
                </button>
              </div>
            </div>
          </div>

          {/* Content Area */}
          <div className="lg:col-span-9">
            <div className="bg-card dark:bg-dark-surface rounded-[2rem] p-8 md:p-10 shadow-sm border border-gray-100 dark:border-dark-border min-h-[500px]">
              {/* Account Settings */}
              {activeTab === "account" && (
                <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="flex justify-between items-center border-b border-gray-100 dark:border-dark-border pb-6">
                    <div>
                      <h2 className="text-2xl font-bold text-text dark:text-dark-text mb-2">
                        Profile Information
                      </h2>
                      <p className="text-gray-500 dark:text-dark-muted">
                        Update your account&apos;s personal information and
                        details.
                      </p>
                    </div>
                    {!isEditing ? (
                      <button
                        onClick={() => setIsEditing(true)}
                        className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold shadow-lg shadow-primary/20 dark:shadow-none transition-all transform hover:-translate-y-0.5"
                      >
                        Edit Profile
                      </button>
                    ) : (
                      <div className="flex gap-3">
                        <button
                          onClick={() => handleConfirmAction("cancel")}
                          className="px-5 py-2.5 border border-gray-300 dark:border-dark-border text-gray-600 dark:text-gray-300 rounded-xl font-medium hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={() => handleConfirmAction("updateProfile")}
                          className="px-5 py-2.5 bg-primary hover:bg-primary/90 text-white rounded-xl font-bold shadow-lg shadow-primary/20 dark:shadow-none transition-all transform hover:-translate-y-0.5"
                        >
                          Save Changes
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-6 mb-8">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full bg-accent dark:bg-gray-800 overflow-hidden border-4 border-white dark:border-gray-700 shadow-lg">
                        <img
                          src={
                            imagePreview ||
                            adminProfile?.image ||
                            placeholderImage
                          }
                          alt="Avatar"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.src = placeholderImage;
                          }}
                        />
                      </div>
                      {isEditing && (
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="absolute bottom-0 right-0 p-2 bg-primary text-white rounded-full shadow-lg hover:bg-opacity-90 transition-transform hover:scale-105"
                        >
                          <Camera size={18} />
                        </button>
                      )}
                    </div>
                    {isEditing && (
                      <div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          accept="image/*"
                          onChange={handleAvatarChange}
                          className="hidden"
                        />
                        <button
                          onClick={() => fileInputRef.current?.click()}
                          className="px-5 py-2 bg-primary hover:bg-primary/90 text-white rounded-xl text-sm font-bold shadow-lg shadow-primary/20 dark:shadow-none transition-all transform hover:-translate-y-0.5"
                        >
                          Change Avatar
                        </button>
                        <button
                          onClick={handleRemoveAvatar}
                          className="ml-3 px-5 py-2 text-red-500 font-medium hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                        >
                          Remove
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-text dark:text-dark-text">
                        Full Name
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={profileForm.name}
                        onChange={handleProfileChange}
                        disabled={!isEditing}
                        className={`w-full px-5 py-3.5 rounded-xl border-none outline-none focus:ring-2 focus:ring-primary/50 text-text dark:text-dark-text font-medium ${
                          isEditing
                            ? "bg-accent dark:bg-gray-900"
                            : "bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
                        }`}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-text dark:text-dark-text">
                        Email Address
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={profileForm.email}
                        onChange={handleProfileChange}
                        disabled={true}
                        className="w-full px-5 py-3.5 rounded-xl bg-gray-100 dark:bg-gray-800 border-none outline-none cursor-not-allowed text-text dark:text-dark-text font-medium"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-text dark:text-dark-text">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={profileForm.phone}
                        onChange={handleProfileChange}
                        disabled={!isEditing}
                        className={`w-full px-5 py-3.5 rounded-xl border-none outline-none focus:ring-2 focus:ring-primary/50 text-text dark:text-dark-text font-medium ${
                          isEditing
                            ? "bg-accent dark:bg-gray-900"
                            : "bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
                        }`}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-text dark:text-dark-text">
                        Gender
                      </label>
                      {isEditing ? (
                        <div className="relative">
                          <select
                            name="gender"
                            value={profileForm.gender}
                            onChange={handleProfileChange}
                            className="w-full px-5 py-3.5 rounded-xl bg-accent dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/50 text-text dark:text-dark-text font-medium appearance-none"
                          >
                            <option value="">Select Gender</option>
                            <option value="Male">Male</option>
                            <option value="Female">Female</option>
                            <option value="Other">Other</option>
                          </select>
                          <div className="absolute right-5 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <CalendarDays size={18} className="text-gray-400" />
                          </div>
                        </div>
                      ) : (
                        <div
                          className={`px-5 py-3.5 rounded-xl text-text dark:text-dark-text font-medium ${
                            profileForm.gender
                              ? "bg-gray-100 dark:bg-gray-800"
                              : "bg-gray-100 dark:bg-gray-800 text-gray-400"
                          }`}
                        >
                          {profileForm.gender || "Not specified"}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-text dark:text-dark-text">
                        Date of Birth
                      </label>
                      {isEditing ? (
                        <div className="relative">
                          <input
                            type="date"
                            name="dateOfBirth"
                            value={profileForm.dateOfBirth}
                            onChange={handleProfileChange}
                            max={new Date().toISOString().split("T")[0]}
                            className="w-full px-5 py-3.5 rounded-xl bg-accent dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/50 text-text dark:text-dark-text font-medium"
                          />
                          <div className="absolute right-5 top-1/2 transform -translate-y-1/2 pointer-events-none">
                            <Calendar size={18} className="text-gray-400" />
                          </div>
                        </div>
                      ) : (
                        <div
                          className={`px-5 py-3.5 rounded-xl text-text dark:text-dark-text font-medium ${
                            profileForm.dateOfBirth
                              ? "bg-gray-100 dark:bg-gray-800"
                              : "bg-gray-100 dark:bg-gray-800 text-gray-400"
                          }`}
                        >
                          {profileForm.dateOfBirth
                            ? new Date(
                                profileForm.dateOfBirth,
                              ).toLocaleDateString("en-US", {
                                year: "numeric",
                                month: "long",
                                day: "numeric",
                              })
                            : "Not specified"}
                        </div>
                      )}
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-text dark:text-dark-text">
                        Address Line 1
                      </label>
                      <input
                        type="text"
                        name="addressLine1"
                        value={profileForm.addressLine1}
                        onChange={handleProfileChange}
                        disabled={!isEditing}
                        className={`w-full px-5 py-3.5 rounded-xl border-none outline-none focus:ring-2 focus:ring-primary/50 text-text dark:text-dark-text font-medium ${
                          isEditing
                            ? "bg-accent dark:bg-gray-900"
                            : "bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
                        }`}
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-text dark:text-dark-text">
                        Address Line 2
                      </label>
                      <input
                        type="text"
                        name="addressLine2"
                        value={profileForm.addressLine2}
                        onChange={handleProfileChange}
                        disabled={!isEditing}
                        className={`w-full px-5 py-3.5 rounded-xl border-none outline-none focus:ring-2 focus:ring-primary/50 text-text dark:text-dark-text font-medium ${
                          isEditing
                            ? "bg-accent dark:bg-gray-900"
                            : "bg-gray-100 dark:bg-gray-800 cursor-not-allowed"
                        }`}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* Notifications Settings */}
              {activeTab === "notifications" && (
                <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="border-b border-gray-100 dark:border-dark-border pb-6">
                    <h2 className="text-2xl font-bold text-text dark:text-dark-text mb-2">
                      Notification Preferences
                    </h2>
                    <p className="text-gray-500 dark:text-dark-muted">
                      Choose how and when you want to be notified.
                    </p>
                  </div>

                  <div className="space-y-4">
                    {/* Email Toggle */}
                    <div className="flex items-center justify-between p-5 bg-accent dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-dark-border">
                      <div className="flex gap-4">
                        <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 flex items-center justify-center">
                          <Bell size={20} />
                        </div>
                        <div>
                          <p className="font-bold text-text dark:text-dark-text">
                            Email Notifications
                          </p>
                          <p className="text-sm text-gray-500 dark:text-dark-muted">
                            Receive daily digests and important updates.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleNotificationToggle("email")}
                        className={`w-14 h-8 rounded-full p-1 transition-colors duration-300 ease-in-out ${
                          emailNotif
                            ? "bg-primary"
                            : "bg-gray-300 dark:bg-gray-700"
                        }`}
                      >
                        <div
                          className={`w-6 h-6 rounded-full bg-white shadow-md transform transition-transform duration-300 ${
                            emailNotif ? "translate-x-6" : "translate-x-0"
                          }`}
                        ></div>
                      </button>
                    </div>

                    {/* SMS Toggle */}
                    <div className="flex items-center justify-between p-5 bg-accent dark:bg-gray-900/50 rounded-2xl border border-gray-100 dark:border-dark-border">
                      <div className="flex gap-4">
                        <div className="w-10 h-10 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 flex items-center justify-center">
                          <Shield size={20} />
                        </div>
                        <div>
                          <p className="font-bold text-text dark:text-dark-text">
                            SMS Alerts
                          </p>
                          <p className="text-sm text-gray-500 dark:text-dark-muted">
                            Get text messages for urgent appointment changes.
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={() => handleNotificationToggle("sms")}
                        className={`w-14 h-8 rounded-full p-1 transition-colors duration-300 ease-in-out ${
                          smsNotif
                            ? "bg-primary"
                            : "bg-gray-300 dark:bg-gray-700"
                        }`}
                      >
                        <div
                          className={`w-6 h-6 rounded-full bg-white shadow-md transform transition-transform duration-300 ${
                            smsNotif ? "translate-x-6" : "translate-x-0"
                          }`}
                        ></div>
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Security Settings */}
              {activeTab === "security" && (
                <div className="space-y-8 animate-in fade-in slide-in-from-right-4 duration-300">
                  <div className="border-b border-gray-100 dark:border-dark-border pb-6">
                    <h2 className="text-2xl font-bold text-text dark:text-dark-text mb-2">
                      Login & Security
                    </h2>
                    <p className="text-gray-500 dark:text-dark-muted">
                      Manage your password and 2-factor authentication.
                    </p>
                  </div>

                  <div className="space-y-6 max-w-lg">
                    <div className="space-y-2">
                      <label className="text-sm font-bold text-text dark:text-dark-text">
                        Current Password
                      </label>
                      <div className="relative">
                        <input
                          type={
                            showPasswords.currentPassword ? "text" : "password"
                          }
                          name="currentPassword"
                          value={securityForm.currentPassword}
                          onChange={handleSecurityChange}
                          className="w-full px-5 py-3.5 rounded-xl bg-accent dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/50 text-text dark:text-dark-text font-medium pr-10"
                          placeholder="••••••••"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            togglePasswordVisibility("currentPassword")
                          }
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                          {showPasswords.currentPassword ? (
                            <EyeOff className="text-xl" />
                          ) : (
                            <Eye className="text-xl" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-bold text-text dark:text-dark-text">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showPasswords.newPassword ? "text" : "password"}
                          name="newPassword"
                          value={securityForm.newPassword}
                          onChange={handleSecurityChange}
                          className="w-full px-5 py-3.5 rounded-xl bg-accent dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/50 text-text dark:text-dark-text font-medium pr-10"
                          placeholder="Min. 8 characters"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            togglePasswordVisibility("newPassword")
                          }
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                          {showPasswords.newPassword ? (
                            <EyeOff className="text-xl" />
                          ) : (
                            <Eye className="text-xl" />
                          )}
                        </button>
                      </div>
                      {renderPasswordStrength()}
                    </div>

                    <div className="space-y-2">
                      <label className="text-sm font-bold text-text dark:text-dark-text">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <input
                          type={
                            showPasswords.confirmPassword ? "text" : "password"
                          }
                          name="confirmPassword"
                          value={securityForm.confirmPassword}
                          onChange={handleSecurityChange}
                          className="w-full px-5 py-3.5 rounded-xl bg-accent dark:bg-gray-900 border-none outline-none focus:ring-2 focus:ring-primary/50 text-text dark:text-dark-text font-medium pr-10"
                          placeholder="Confirm your new password"
                        />
                        <button
                          type="button"
                          onClick={() =>
                            togglePasswordVisibility("confirmPassword")
                          }
                          className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        >
                          {showPasswords.confirmPassword ? (
                            <EyeOff className="text-xl" />
                          ) : (
                            <Eye className="text-xl" />
                          )}
                        </button>
                      </div>
                    </div>

                    <div className="pt-2">
                      <button
                        onClick={() => handleConfirmAction("updatePassword")}
                        disabled={
                          !securityForm.currentPassword ||
                          !securityForm.newPassword ||
                          !securityForm.confirmPassword
                        }
                        className={`px-8 py-3.5 text-white rounded-xl font-bold transition-all transform hover:-translate-y-0.5 shadow-lg shadow-primary/20 dark:shadow-none ${
                          !securityForm.currentPassword ||
                          !securityForm.newPassword ||
                          !securityForm.confirmPassword
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-primary hover:bg-primary/90"
                        }`}
                      >
                        Update Password
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Confirmation Modal */}
      {showConfirmModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-dark-surface rounded-2xl shadow-2xl p-6 w-full max-w-md border border-gray-100 dark:border-dark-border">
            <h2 className="text-xl font-bold mb-4 text-text dark:text-white">
              Confirm Action
            </h2>
            <p className="text-gray-600 dark:text-dark-muted mb-8">
              {modalType === "updateProfile"
                ? "Are you sure you want to update your profile details?"
                : modalType === "updatePassword"
                  ? "Are you sure you want to change your password?"
                  : "Are you sure you want to discard unsaved changes?"}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => handleModalAction(false)}
                className="px-5 py-2 border border-gray-300 dark:border-dark-border text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => handleModalAction(true)}
                className="px-5 py-2 bg-primary text-white rounded-lg hover:bg-opacity-90 transition-colors"
              >
                Confirm
              </button>
            </div>
          </div>
          <div
            className="fixed inset-0 -z-10"
            onClick={() => setShowConfirmModal(false)}
          />
        </div>
      )}
    </div>
  );
};

export default Settings;
